// =============================================================================
// app/src/lib/recorte.ts — a foto da peça SEM o fundo branco, feita no aparelho.
//
// Pedido do dono (01/10/2026): "quando eu abrisse as vendas, os itens
// cadastrados viessem com uma imagem ao lado, sem fundo branco". As fotos do
// cadastro são JPEG de catálogo, quase sempre em fundo branco — num app de
// fundo preto, o quadrado branco grita mais que a peça.
//
// O mesmo método do scripts/calotas-sem-fundo.py (que recortou as calotas do
// mapa), em versão geral e sem dependência:
//   1. o fundo é a cor da MARGEM da foto, e só vale se for clara e uniforme —
//      foto tirada no balcão, de fundo qualquer, volta como está (null);
//   2. é fundo o que encosta na margem com cor parecida (preenchimento);
//   3. SÓ com `vaos: true`: um vão por DENTRO da peça (entre os raios de uma
//      calota) some quando a borda dele é escura. Desligado por padrão: num
//      frasco preto, o rótulo branco também tem borda escura e sumiria junto
//      (o teste mostra) — para foto de produto em geral, o buraco branco é o
//      mal menor. As calotas já têm recorte pronto (fotosDasCalotas.ts);
//   4. a borda ganha um degradê de 1 px, e a imagem é cortada rente à peça.
//
// Tudo aqui é conta sobre os pixels: o canvas e a rede ficam em fotoRecortada.ts.
// =============================================================================

export interface Pixels {
  /** RGBA, 4 bytes por pixel, linha a linha (o formato do ImageData). */
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** Distância (maior diferença de canal) até a cor do fundo que ainda é fundo. */
const T_FUNDO = 30;
/**
 * Com fundo QUASE BRANCO, a régua aperta: a 30, uma peça clara (reservatório,
 * plástico creme, o vidro da lâmpada) virava fundo e a miniatura mostrava só a
 * tampa (revisão do #254). Fundo de estúdio mais cinzento segue com 30.
 */
const T_FUNDO_BRANCO = 14;
const FUNDO_BRANCO = 240;
/** Pedaço solto menor que isto (fração da foto) é sujeira, não peça. */
const PEDACO_MINIMO = 0.002;
/** O maior pedaço tem de ser quase toda a peça; senão ela foi picada. */
const PEDACO_PRINCIPAL = 0.85;
/** Para o vão por dentro da peça, mais exigente. */
const T_VAO = 14;
/** Luminância média da borda do vão abaixo da qual ele é vão de verdade. */
const BORDA_ESCURA = 215;
/** O fundo só conta se for claro e a margem for quase toda dessa cor. */
const FUNDO_MINIMO = 200;
const MARGEM_UNIFORME = 0.8;
/** Folga ao redor da peça depois do corte, em fração do lado maior. */
const FOLGA = 0.04;

const lum = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b;

function mediana(v: number[]): number {
  const s = [...v].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/** A cor do fundo pela margem da foto, ou null se a margem não é um fundo claro e liso. */
export function corDoFundo({ data, width: w, height: h }: Pixels): [number, number, number] | null {
  if (w < 3 || h < 3) return null;
  const r: number[] = [], g: number[] = [], b: number[] = [];
  const pega = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    r.push(data[i]!); g.push(data[i + 1]!); b.push(data[i + 2]!);
  };
  for (let x = 0; x < w; x++) { pega(x, 0); pega(x, h - 1); }
  for (let y = 1; y < h - 1; y++) { pega(0, y); pega(w - 1, y); }
  const cor: [number, number, number] = [mediana(r), mediana(g), mediana(b)];
  if (lum(...cor) < FUNDO_MINIMO) return null;
  let iguais = 0;
  for (let k = 0; k < r.length; k++) {
    if (Math.max(Math.abs(r[k]! - cor[0]), Math.abs(g[k]! - cor[1]), Math.abs(b[k]! - cor[2])) < T_FUNDO) iguais++;
  }
  return iguais / r.length >= MARGEM_UNIFORME ? cor : null;
}

/**
 * A máscara da peça: 1 = peça, 0 = fundo. null quando a foto não tem fundo
 * claro e liso (aí a foto deve aparecer como está).
 */
export function mascaraDaPeca(px: Pixels, { vaos = false }: { vaos?: boolean } = {}): Uint8Array | null {
  const cor = corDoFundo(px);
  if (!cor) return null;
  const { data, width: w, height: h } = px;
  const n = w * h;
  const tFundo = lum(...cor) >= FUNDO_BRANCO ? T_FUNDO_BRANCO : T_FUNDO;
  const dist = new Uint8Array(n);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    dist[p] = Math.max(Math.abs(data[i]! - cor[0]), Math.abs(data[i + 1]! - cor[1]), Math.abs(data[i + 2]! - cor[2]));
  }

  // 2. O fundo que encosta na margem (preenchimento em largura, 4 vizinhos).
  const fundo = new Uint8Array(n);
  const fila = new Int32Array(n);
  let ini = 0, fim = 0;
  const semeia = (p: number) => { if (!fundo[p] && dist[p]! < tFundo) { fundo[p] = 1; fila[fim++] = p; } };
  for (let x = 0; x < w; x++) { semeia(x); semeia((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { semeia(y * w); semeia(y * w + w - 1); }
  while (ini < fim) {
    const p = fila[ini++]!;
    const x = p % w, y = (p - x) / w;
    if (x > 0) semeia(p - 1);
    if (x < w - 1) semeia(p + 1);
    if (y > 0) semeia(p - w);
    if (y < h - 1) semeia(p + w);
  }

  const peca = new Uint8Array(n);
  for (let p = 0; p < n; p++) peca[p] = fundo[p] ? 0 : 1;

  if (!vaos) return peca;

  // 3. Os vãos por dentro: regiões claras soltas, de borda escura.
  const visto = new Uint8Array(n);
  const minimo = Math.max(12, Math.round(n * 0.0008));
  const raio = Math.max(2, Math.round(Math.min(w, h) / 120));
  for (let s = 0; s < n; s++) {
    if (visto[s] || !peca[s] || dist[s]! >= T_VAO) continue;
    ini = 0; fim = 0;
    visto[s] = 1; fila[fim++] = s;
    while (ini < fim) {
      const p = fila[ini++]!;
      const x = p % w, y = (p - x) / w;
      const vizinho = (q: number) => {
        if (!visto[q] && peca[q] && dist[q]! < T_VAO) { visto[q] = 1; fila[fim++] = q; }
      };
      if (x > 0) vizinho(p - 1);
      if (x < w - 1) vizinho(p + 1);
      if (y > 0) vizinho(p - w);
      if (y < h - 1) vizinho(p + w);
    }
    if (fim < minimo) continue;
    // A borda: o pixel a `raio` de distância, fora da região, nas 4 direções.
    const regiao = new Set<number>();
    for (let k = 0; k < fim; k++) regiao.add(fila[k]!);
    let soma = 0, conta = 0;
    for (let k = 0; k < fim; k++) {
      const p = fila[k]!;
      const x = p % w, y = (p - x) / w;
      for (const [dx, dy] of [[raio, 0], [-raio, 0], [0, raio], [0, -raio]] as const) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const q = ny * w + nx;
        if (regiao.has(q)) continue;
        const i = q * 4;
        soma += lum(data[i]!, data[i + 1]!, data[i + 2]!);
        conta++;
      }
    }
    if (conta > 0 && soma / conta < BORDA_ESCURA) {
      for (let k = 0; k < fim; k++) peca[fila[k]!] = 0;
    }
  }
  return peca;
}

/**
 * A peça saiu inteira? Conta os pedaços (vizinhança de 4) do que sobrou: se o
 * maior não é quase tudo, o recorte picou a peça — um kit de várias peças, ou
 * uma peça clara que o fundo comeu pela metade. Aí a original é mais honesta.
 */
export function pecaInteira(mascara: Uint8Array, w: number, h: number): boolean {
  const n = w * h;
  const visto = new Uint8Array(n);
  const fila = new Int32Array(n);
  const minimo = Math.max(4, Math.round(n * PEDACO_MINIMO));
  let total = 0, maior = 0;
  for (let s = 0; s < n; s++) {
    if (visto[s] || !mascara[s]) continue;
    let ini = 0, fim = 0;
    visto[s] = 1; fila[fim++] = s;
    while (ini < fim) {
      const p = fila[ini++]!;
      const x = p % w, y = (p - x) / w;
      const vizinho = (q: number) => { if (!visto[q] && mascara[q]) { visto[q] = 1; fila[fim++] = q; } };
      if (x > 0) vizinho(p - 1);
      if (x < w - 1) vizinho(p + 1);
      if (y > 0) vizinho(p - w);
      if (y < h - 1) vizinho(p + w);
    }
    if (fim < minimo) continue;
    total += fim;
    if (fim > maior) maior = fim;
  }
  return total > 0 && maior >= total * PEDACO_PRINCIPAL;
}

/** O retângulo que contém a peça, com folga; null se não sobrou peça. */
export function caixaDaPeca(mascara: Uint8Array, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!mascara[y * w + x]) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return null;
  const folga = Math.round(Math.max(x1 - x0, y1 - y0) * FOLGA);
  const x = Math.max(0, x0 - folga), y = Math.max(0, y0 - folga);
  return { x, y, w: Math.min(w, x1 + folga + 1) - x, h: Math.min(h, y1 + folga + 1) - y };
}

/**
 * A foto sem o fundo, cortada rente à peça. null quando não dá para recortar
 * (fundo escuro ou irregular, ou nada sobrou) — quem chama mostra a original.
 */
export function recortar(px: Pixels, opcoes: { vaos?: boolean } = {}): Pixels | null {
  const mascara = mascaraDaPeca(px, opcoes);
  if (!mascara) return null;
  const { data, width: w, height: h } = px;
  // Se quase tudo virou fundo (ou quase nada), a foto não é de catálogo.
  let pecas = 0;
  for (let p = 0; p < mascara.length; p++) pecas += mascara[p]!;
  if (pecas < mascara.length * 0.02) return null;
  if (!pecaInteira(mascara, w, h)) return null;
  const caixa = caixaDaPeca(mascara, w, h);
  if (!caixa) return null;

  const saida = new Uint8ClampedArray(caixa.w * caixa.h * 4);
  for (let y = 0; y < caixa.h; y++) {
    for (let x = 0; x < caixa.w; x++) {
      const sx = x + caixa.x, sy = y + caixa.y;
      const p = sy * w + sx;
      const i = p * 4, o = (y * caixa.w + x) * 4;
      // 4. Degradê de 1 px: a média da máscara 3×3 vira o alfa.
      let soma = 0, conta = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = sx + dx, ny = sy + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          soma += mascara[ny * w + nx]!; conta++;
        }
      }
      saida[o] = data[i]!; saida[o + 1] = data[i + 1]!; saida[o + 2] = data[i + 2]!;
      saida[o + 3] = mascara[p] ? Math.round((255 * soma) / conta) : Math.round((128 * soma) / conta);
    }
  }
  return { data: saida, width: caixa.w, height: caixa.h };
}
